<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { HTTPError, requestJSON } from '../core/http';
import { useAppConfig } from '../composables/use-app-config';
import { useCartStore } from '../stores/cart-store';
import { useOrdersStore } from '../stores/orders-store';
import { useSessionStore } from '../stores/session-store';
import { useUserStore } from '../stores/user-store';
import InputField from '@shared/components/InputField.vue';
import TextareaField from '@shared/components/TextareaField.vue';
import LoadingButton from '@shared/components/LoadingButton.vue';
import Alert from '@shared/components/Alert.vue';
import TableSelector from '../components/table/TableSelector.vue';
import { isFeatureEnabled } from '@shared/config/feature-flags';

const props = defineProps<{
  sessionReady: boolean;
  editingOrderId?: string;
}>();

const emit = defineEmits<{
  (e: 'success', orderId: string | number): void;
  (e: 'back'): void;
  (e: 'open-profile'): void;
  (e: 'open-table-selector'): void;
  (e: 'edit-item', item: any, index: number): void;
}>();

const userStore = useUserStore();
const cartStore = useCartStore();
const sessionStore = useSessionStore();
const ordersStore = useOrdersStore();

const editingOrder = computed(() => {
  const orderId = String(props.editingOrderId || '').trim();
  return orderId ? ordersStore.getOrderById(orderId) : undefined;
});
const isEditingOrder = computed(() => Boolean(editingOrder.value));

const isSubmitting = ref(false);
const errorMessage = ref<string | null>(null);
const successMessage = ref<string | null>(null);
const orderConfirmed = ref(false);
const validationAttempted = ref(false);
const nameFieldRef = ref<{ focus: () => void } | null>(null);
const phoneFieldRef = ref<{ focus: () => void } | null>(null);

const form = reactive({
  name: '',
  email: '',
  phone: '',
  notes: '',
  table_number: '',
  table_display: '',
});

onMounted(() => {
  if (userStore.user) {
    form.name = userStore.user.name;
    form.email = userStore.user.email;
    form.phone = userStore.user.phone || '';
  }

  form.table_number = String(
    sessionStore.tableContext?.table_code || sessionStore.tableContext?.table_id || '',
  ).trim();
  form.table_display = form.table_number;

  if (form.table_display && form.table_display === form.table_number) {
    resolveTableCode();
  }
});

const resolveTableCode = async () => {
  try {
    const response = await requestJSON<{ tables: Array<{ id: string, table_number: string }> }>('/api/tables');
    const table = response.tables.find(t => t.id === form.table_number);
    if (table && table.table_number) {
      form.table_display = table.table_number;
      // Also update session store context if possible
      sessionStore.setTableContext(form.table_number, sessionStore.tableContext?.source || 'unknown', table.table_number);
    }
  } catch (err) {
    // silently fail
  }
};

watch(
  () => userStore.user,
  (newUser) => {
    if (newUser) {
      form.name = newUser.name;
      form.email = newUser.email;
      form.phone = newUser.phone || '';
    }
  },
  { deep: true },
);

watch(
  () => sessionStore.tableContext,
  (nextContext) => {
    const tableId = String(nextContext?.table_id || '').trim();
    const tableCode = String(nextContext?.table_code || '').trim();
    if (tableId || tableCode) {
      form.table_number = tableCode || tableId;
      form.table_display = tableCode || tableId;
    }
  },
  { deep: true },
);

const isPhoneDisabled = computed(() => {
  if (!userStore.isAuthenticated) return false;
  const digits = (userStore.user?.phone || '').replace(/\D/g, '');
  return digits.length === 10;
});

const isFormValid = computed(() => {
  const phoneDigits = form.phone.replace(/\D/g, '');
  const hasValidPhone = phoneDigits.length === 10 || (userStore.isAuthenticated && phoneDigits.length === 0);
  return (
    form.name.trim().length > 0 &&
    form.table_number.trim().length > 0 &&
    hasValidPhone
  );
});

const nameError = computed(() => {
  if (!validationAttempted.value || form.name.trim()) return null;
  return 'Ingresa un nombre para identificar la orden.';
});

const phoneError = computed(() => {
  const digits = form.phone.replace(/\D/g, '');
  if (digits.length === 0) {
    return validationAttempted.value && !userStore.isAuthenticated ? 'Ingresa un celular de 10 dígitos.' : null;
  }
  if (digits.length < 10) return `Se requieren 10 dígitos (${digits.length}/10)`;
  if (digits.length > 10) return 'El número no puede exceder 10 dígitos';
  return null;
});

const canSubmit = computed(() => {
  return (
    userStore.isAuthenticated &&
    !isSubmitting.value &&
    cartStore.items.length > 0
  );
});

const confirmButtonLabel = computed(() => {
  if (isEditingOrder.value) {
    return 'Guardar cambios';
  }
  if (!userStore.isAuthenticated) {
    return 'Inicia sesión para confirmar';
  }
  if (isSubmitting.value) {
    return 'Enviando pedido…';
  }
  if (!sessionStore.sessionReady && !form.table_number.trim()) {
    return 'Selecciona una mesa para continuar';
  }
  const phoneDigits = form.phone.replace(/\D/g, '');
  if (phoneDigits.length > 0 && phoneDigits.length < 10) {
    return 'Ingresa celular de 10 dígitos';
  }
  return 'Confirmar y Enviar Pedido';
});

const subtotal = computed(() => {
  return cartStore.items.reduce(
    (acc, item) => acc + (item.price + (Number(item.extrasTotal) || 0)) * item.quantity,
    0,
  );
});

const appConfig = useAppConfig();

const formattedTotal = computed(() => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: appConfig.config.currency_code || 'USD',
  }).format(subtotal.value);
});

const setUserFacingError = (err: unknown): void => {
  if (err instanceof HTTPError && err.responseData && typeof err.responseData === 'object') {
    const responseData = err.responseData as Record<string, any>;
    const details = (responseData.details || {}) as Record<string, any>;
    const code = String(details.code || '');

    if (code === 'TABLE_SESSION_MISMATCH' || code === 'TABLE_LOCATION_MISMATCH') {
      form.table_number = details.current_table_code || details.current_table_id || form.table_number;
    }

    const messages: Record<string, string> = {
      TABLE_REQUIRED: 'Selecciona una mesa para continuar. Usa el selector de mesa arriba.',
      TABLE_LOCKED_BY_QR: 'Esta mesa fue asignada por código QR y no puede cambiarse. Escanea el QR de la mesa asignada.',
      KIOSK_TABLE_NOT_CONFIGURED: 'Este kiosko no tiene una mesa configurada. Contacta al administrador del restaurante.',
      KIOSK_TABLE_LOCATION_MISMATCH: 'La mesa seleccionada no corresponde a este kiosko. Elige una mesa de esta área.',
      TABLE_LOCATION_MISMATCH: 'La mesa cambió en tu sesión activa. Selecciona la mesa nuevamente para confirmar.',
      TABLE_SESSION_MISMATCH: 'Tu mesa cambió. Actualizamos la sesión, intenta confirmar de nuevo.',
      SESSION_CLOSED: 'Esta sesión ya fue cerrada. Inicia una nueva orden.',
      AUTH_REQUIRED: 'Debes iniciar sesión para continuar. Haz clic en "Iniciar Sesión" abajo.',
      PAYMENT_INVALID_AMOUNT: 'El monto de pago no es válido. Verifica el total e intenta de nuevo.',
      IDEMPOTENCY_KEY_REQUIRED: 'Error de seguridad: falta clave de idempotencia. Recarga la página e intenta de nuevo.',
    };

    const defaultMsg = String(responseData.error || err.message || 'Error al procesar el pedido');
    errorMessage.value = messages[code] || defaultMsg;
    return;
  }

  // Network errors
  if (err instanceof TypeError && err.message === 'Failed to fetch') {
    errorMessage.value = 'No se pudo conectar con el servidor. Verifica tu conexión a internet e intenta de nuevo.';
    return;
  }

  errorMessage.value = err instanceof Error ? err.message : 'Error inesperado. Intenta de nuevo o contacta a soporte.';
};

const buildModificationChanges = () => {
  const originalItems = editingOrder.value?.items || [];
  const currentItems = cartStore.items;
  const currentByOrderItemId = new Map(
    currentItems
      .filter((item) => item.order_item_id != null)
      .map((item) => [String(item.order_item_id), item]),
  );

  const itemsToRemove = originalItems
    .filter((item) => !currentByOrderItemId.has(String(item.id)))
    .map((item) => item.id);
  const itemsToUpdate = originalItems.flatMap((item) => {
    const current = currentByOrderItemId.get(String(item.id));
    if (!current || Number(current.quantity) === Number(item.quantity)) return [];
    return [{ order_item_id: item.id, quantity: Number(current.quantity) }];
  });
  const itemsToAdd = currentItems
    .filter((item) => item.order_item_id == null)
    .map((item) => ({
      menu_item_id: item.menu_item_id ?? item.id,
      quantity: Number(item.quantity || 1),
      modifiers: (item.modifiers || []).map((modifier) =>
        typeof modifier === 'object' && modifier !== null
          ? modifier
          : { modifier_id: modifier, quantity: 1 },
      ),
      package_components: item.package_components || [],
      special_instructions: item.special_instructions || '',
    }));

  return { items_to_add: itemsToAdd, items_to_remove: itemsToRemove, items_to_update: itemsToUpdate };
};

// #980 (C8): the summary CTA 'Selecciona una mesa' used to be a no-op when the
// user was already on this view. Scrolls to and focuses the table control so
// the click always has an observable effect.
const tableSelectorHostRef = ref<HTMLElement | null>(null);

const focusTableSelector = async (): Promise<void> => {
  await nextTick();
  const host = tableSelectorHostRef.value;
  if (!host) return;
  const control = host.querySelector('#table-select') as HTMLElement | null;
  const target = control && typeof control.focus === 'function' ? control : host;
  // jsdom (unit tests) and some embedded webviews lack scrollIntoView.
  if (typeof target.scrollIntoView === 'function') {
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  target.focus({ preventScroll: true });
};

defineExpose({ focusTableSelector });

const confirmOrder = async () => {
  if (isSubmitting.value) {
    return;
  }

  if (!userStore.isAuthenticated) {
    errorMessage.value =
      'Debes iniciar sesión para confirmar tu pedido. Usa el botón «Iniciar Sesión» arriba.';
    emit('open-profile');
    return;
  }

  if (!props.sessionReady || !sessionStore.sessionReady) {
    if (form.table_number.trim()) {
      try {
        await sessionStore.initSession(form.table_number.trim());
      } catch (err) {
        errorMessage.value = 'No se pudo iniciar la sesión de mesa. Selecciona una mesa nuevamente.';
        return;
      }
    } else {
      errorMessage.value = 'Selecciona una mesa antes de confirmar el pedido.';
      validationAttempted.value = true;
      emit('open-table-selector');
      return;
    }
  }

  if (!isFormValid.value || cartStore.items.length === 0) {
    validationAttempted.value = true;
    if (!form.table_number) {
      errorMessage.value = 'Selecciona una mesa antes de confirmar el pedido.';
    } else if (cartStore.items.length === 0) {
      errorMessage.value = 'Agrega productos al carrito antes de confirmar.';
    } else if (!form.name.trim()) {
      errorMessage.value = 'Ingresa el nombre para la orden y revisa los campos marcados.';
    } else if (form.phone.replace(/\D/g, '').length !== 10) {
      errorMessage.value = 'Ingresa un celular válido de 10 dígitos y revisa el campo marcado.';
    } else {
      errorMessage.value = 'Completa los datos requeridos antes de confirmar.';
    }
    await nextTick();
    const invalidField = !form.name.trim() ? nameFieldRef : phoneFieldRef;
    if (typeof invalidField.value?.focus === 'function') {
      invalidField.value.focus();
    }
    return;
  }

  isSubmitting.value = true;
  errorMessage.value = null;

  try {
    if (isEditingOrder.value && props.editingOrderId) {
      const changes = buildModificationChanges();
      if (
        changes.items_to_add.length === 0 &&
        changes.items_to_remove.length === 0 &&
        changes.items_to_update.length === 0
      ) {
        errorMessage.value = 'Realiza un cambio antes de guardar la orden.';
        return;
      }

      await requestJSON(`/api/customer/orders/${encodeURIComponent(props.editingOrderId)}/modify`, {
        method: 'POST',
        body: { changes },
      });
      cartStore.clearCart('submitted');
      emit('success', props.editingOrderId);
      return;
    }

    const response = await cartStore.submitCart({
      notes: form.notes || undefined,
      tableNumber: form.table_number || undefined,
      submitOrigin: 'checkout_view',
    });

    // order_id is the canonical identifier; display_order_id is a visual folio
    // and must never be used as the canonical identity.
    const orderId = String(response?.order_id || '').trim();
    if (!orderId) {
      throw new Error('No se recibió identificador de orden');
    }

    emit('success', orderId);
  } catch (err) {
    setUserFacingError(err);
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div class="checkout-view">
    <header class="checkout-header">
      <button type="button" class="btn-back" aria-label="Volver al menú" @click="emit('back')">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
        Volver al menú
      </button>
      <h1>{{ isEditingOrder ? 'Editar Pedido' : 'Confirmar Pedido' }}</h1>
    </header>

    <!-- Always visible when guest: previous condition hid the banner when sessionReady -->
    <Alert v-if="!userStore.isAuthenticated" variant="warning" class="mb-4" data-testid="checkout-auth-required">
      <div class="session-alert-content">
        <span>Para confirmar tu pedido necesitas una cuenta. Inicia sesión o regístrate antes de continuar — tus datos del carrito se conservan.</span>
        <button type="button" class="btn-login" @click="emit('open-profile')">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/>
          </svg>
          Iniciar Sesión
        </button>
      </div>
    </Alert>

    <div class="checkout-grid">
      <section class="checkout-form-section">
        <Alert v-if="errorMessage" type="error" class="mb-4" role="alert" aria-live="assertive" aria-atomic="true">
          {{ errorMessage }}
        </Alert>

        <div class="card form-card">
          <h3>Tus Datos</h3>
          <div class="form-fields">
            <InputField
              id="checkout-name"
              ref="nameFieldRef"
              v-model="form.name"
              label="Nombre para la orden *"
              placeholder="Ej. Juan Pérez"
              :error="nameError || undefined"
              :disabled="userStore.isAuthenticated"
              required
            />

            <div class="form-row">
              <InputField
                v-model="form.email"
                label="Correo (opcional)"
                type="email"
                placeholder="tu@email.com"
                :disabled="userStore.isAuthenticated"
              />
              <div>
                <InputField
                  id="checkout-phone"
                  ref="phoneFieldRef"
                  v-model="form.phone"
                  label="Celular (10 dígitos) *"
                  type="tel"
                  placeholder="5512345678"
                  :maxlength="10"
                  :error="phoneError || undefined"
                  :disabled="isPhoneDisabled"
                  required
                />
              </div>
            </div>

            <div ref="tableSelectorHostRef" class="table-selector-host" data-testid="table-selector-host">
              <TableSelector v-if="isFeatureEnabled('tableSelectorEnabled')" />
            </div>
            <InputField
              v-if="!isFeatureEnabled('tableSelectorEnabled')"
              v-model="form.table_display"
              label="Mesa"
              type="text"
              placeholder="ID de mesa"
              :disabled="true"
            />

            <TextareaField
              v-model="form.notes"
              label="Instrucciones especiales"
              placeholder="¿Alguna alergia o detalle sobre tu pedido?"
              :rows="3"
            />
          </div>
        </div>
      </section>

      <section class="checkout-summary-section">
        <div class="card summary-card">
          <h3>Resumen de Orden</h3>
          <ul class="summary-items">
            <li v-for="(item, index) in cartStore.items" :key="item.id || index" class="summary-item">
              <div class="item-info">
                <span class="item-name">{{ item.name }}</span>
              </div>
              <div class="item-actions">
                <div class="item-qty-controls">
                  <button
                    type="button"
                    class="btn-qty-step"
                    :aria-label="`Restar ${item.name}`"
                    title="Disminuir cantidad"
                    @click="cartStore.updateItemQuantity(index, -1)"
                  >
                    -
                  </button>
                  <span class="item-qty-count">{{ item.quantity }}</span>
                  <button
                    type="button"
                    class="btn-qty-step"
                    :aria-label="`Sumar ${item.name}`"
                    title="Aumentar cantidad"
                    @click="cartStore.updateItemQuantity(index, 1)"
                  >
                    +
                  </button>
                </div>
                <button
                  type="button"
                  class="btn-edit-summary"
                  :aria-label="`Editar ${item.name}`"
                  title="Editar opciones"
                  @click="emit('edit-item', item, index)"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                </button>
                <button
                  type="button"
                  class="btn-remove-summary"
                  :aria-label="`Eliminar ${item.name}`"
                  title="Eliminar del carrito"
                  @click="cartStore.removeItem(index)"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
                <span class="item-price">${{ ((item.price + (item.extrasTotal || 0)) * item.quantity).toFixed(2) }}</span>
              </div>
            </li>
          </ul>

          <div class="total-breakdown">
            <div class="total-row">
              <span>Subtotal</span>
              <span>{{ formattedTotal }}</span>
            </div>
            <p class="tax-note">Impuestos incluidos</p>
            <div class="total-row total-main">
              <span>Total a pagar</span>
              <span>{{ formattedTotal }}</span>
            </div>
          </div>

          <LoadingButton
            variant="primary"
            class="btn-confirm"
            :loading="isSubmitting"
            :disabled="!canSubmit"
            :label="confirmButtonLabel"
            :aria-live="isSubmitting ? 'polite' : undefined"
            @click="confirmOrder"
          >
            {{ confirmButtonLabel }}
          </LoadingButton>

          <p class="privacy-note">
            Al confirmar, aceptas nuestros términos de servicio.
          </p>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.checkout-view {
  max-width: 1000px;
  margin: 0 auto;
  padding: 1.5rem;
}

/* #980 (C8): host is a programmatic focus target when the native select is
   unavailable (fixed QR/kiosk mode); ring shows on focus-visible only. */
.table-selector-host {
  outline: none;
}

.table-selector-host:focus-visible {
  outline: 2px solid #e05a2a;
  outline-offset: 2px;
}

.checkout-header {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.checkout-header h1 {
  font-size: 1.5rem;
  font-weight: 800;
  margin: 0;
}

.btn-back {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: var(--muted);
  font-weight: 600;
  cursor: pointer;
  padding: 0.5rem;
  min-height: 44px;
  border-radius: 8px;
}
.btn-back:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.4);
}

.checkout-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
}

@media (min-width: 768px) {
  .checkout-grid {
    grid-template-columns: 1.5fr 1fr;
  }
}

.card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid var(--border);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.card h3 {
  margin-top: 0;
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
  border-bottom: 1px solid var(--border);
  padding-bottom: 0.75rem;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

@media (min-width: 480px) {
  .form-row {
    grid-template-columns: 1fr 1fr;
  }
}

.summary-items {
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem 0;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px dashed var(--border);
}

.item-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.btn-edit-summary {
  background: none;
  border: 1px solid #dbe2ea;
  border-radius: 6px;
  color: #2563eb;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.2rem 0.5rem;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.btn-edit-summary:hover {
  background: #eff6ff;
  border-color: #2563eb;
}

.btn-remove-summary {
  background: none;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  padding: 0.2rem 0.35rem;
  cursor: pointer;
  transition: background-color 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-remove-summary:hover {
  background: #fef2f2;
  border-color: #dc2626;
}

.item-qty-controls {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  background: #f1f5f9;
  border-radius: 6px;
  padding: 0.15rem 0.3rem;
}

.btn-qty-step {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 800;
  color: #334155;
  cursor: pointer;
  line-height: 1;
  transition: all 0.15s ease;
}

.btn-qty-step:hover {
  background: #e2e8f0;
  border-color: #94a3b8;
}

.item-qty-count {
  font-size: 0.85rem;
  font-weight: 700;
  color: #0f172a;
  min-width: 18px;
  text-align: center;
}

.item-qty {
  font-weight: 700;
  color: var(--primary);
  margin-right: 0.5rem;
}

.total-breakdown {
  background: #f8fafc;
  padding: 1rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
}

.total-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
  color: var(--muted);
}

.total-main {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border);
  font-weight: 800;
  color: var(--text);
  font-size: 1.25rem;
}

.tax-note {
  margin: 0.25rem 0;
  font-size: 0.78rem;
  color: var(--muted);
}

.btn-confirm {
  width: 100%;
  min-height: 48px;
}

.privacy-note {
  margin-top: 1rem;
  font-size: 0.78rem;
  color: var(--muted);
  text-align: center;
}

.session-alert-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  width: 100%;
}

.btn-login {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  background: linear-gradient(135deg, #e05a2a 0%, #f7522a 100%);
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 0.5rem 1.1rem;
  min-height: 38px;
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(255, 107, 53, 0.35), 0 1px 2px rgba(0,0,0,0.08);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  white-space: nowrap;
}

.btn-login:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(255, 107, 53, 0.45), 0 2px 4px rgba(0,0,0,0.1);
}

.btn-login:active {
  transform: translateY(0);
  box-shadow: 0 1px 4px rgba(255, 107, 53, 0.3);
}

.btn-login:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.4), 0 2px 8px rgba(255, 107, 53, 0.35);
}
</style>
